import { format, subDays } from 'date-fns';

export function computeCustomerAnalytics(users) {
  const total = users.length;
  const premium = users.filter((u) => u.subscription?.isPremium).length;
  const suspended = users.filter((u) => u.isActive === false).length;
  const verified = users.filter((u) => u.isEmailVerified).length;

  const signupTrend = Array.from({ length: 30 }).map((_, index) => {
    const day = subDays(new Date(), 29 - index);
    const label = format(day, 'MMM d');
    const count = users.filter((u) => u.createdAt && format(new Date(u.createdAt), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')).length;
    let running = 0;
    users.forEach((u) => {
      if (u.createdAt && new Date(u.createdAt) <= day) running += 1;
    });
    return { label, signups: count, cumulative: running };
  });

  const segments = [
    { name: 'Premium', value: premium },
    { name: 'Free', value: total - premium },
    { name: 'Suspended', value: suspended },
  ];

  const activationRate = total ? Math.round((verified / total) * 100) : 0;
  const premiumRate = total ? Math.round((premium / total) * 100) : 0;

  return { total, premium, suspended, verified, signupTrend, segments, activationRate, premiumRate };
}

const renewalKey = (sub) => sub.renewalStatus || sub.status || 'pending';
const storeKey = (sub) => sub.store || sub.platform || 'unknown';
const entitlementKey = (sub) => sub.entitlementType || (sub.isPremium ? 'premium' : sub.isTrialActive ? 'trial' : 'none');

export function computeSubscriptionAnalytics(subscriptions, users) {
  const userLookup = Object.fromEntries(users.map((u) => [u.id, u]));
  const active = subscriptions.filter((s) => renewalKey(s) === 'active' && s.isPremium).length;
  const cancelled = subscriptions.filter((s) => renewalKey(s) === 'cancelled').length;
  const expired = subscriptions.filter((s) => renewalKey(s) === 'expired').length;
  const grace = subscriptions.filter((s) => renewalKey(s) === 'grace').length;
  const unverified = subscriptions.filter((s) => renewalKey(s) === 'unverified' || renewalKey(s) === 'pending').length;
  const trialActive = subscriptions.filter((s) => s.trialStatus === 'active' || s.isTrialActive).length;

  const statusBreakdown = [
    { status: 'Active', count: active },
    { status: 'Grace', count: grace },
    { status: 'Cancelled', count: cancelled },
    { status: 'Expired', count: expired },
    { status: 'Unverified', count: unverified },
  ].filter((row) => row.count > 0);

  const platformBreakdown = Object.entries(
    subscriptions.reduce((acc, s) => {
      const platform = storeKey(s);
      acc[platform] = (acc[platform] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, count]) => ({ name, count }));

  const entitlementBreakdown = Object.entries(
    subscriptions.reduce((acc, s) => {
      const key = entitlementKey(s);
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, count]) => ({ name, count }));

  const renewalTimeline = subscriptions
    .filter((s) => s.expiresAt)
    .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt))
    .slice(0, 12)
    .map((s) => ({
      customer: userLookup[s.userId]?.name || userLookup[s.userId]?.email || 'Unknown',
      expires: format(new Date(s.expiresAt), 'MMM d, yyyy'),
      status: renewalKey(s),
      entitlement: entitlementKey(s),
      store: storeKey(s),
      isPremium: s.isPremium,
      lastVerifiedAt: s.lastVerifiedAt ? format(new Date(s.lastVerifiedAt), 'MMM d, yyyy') : null,
    }));

  const churnRate = subscriptions.length
    ? Math.round(((cancelled + expired) / subscriptions.length) * 100)
    : 0;

  return {
    active,
    cancelled,
    expired,
    grace,
    unverified,
    trialActive,
    statusBreakdown,
    platformBreakdown,
    entitlementBreakdown,
    renewalTimeline,
    churnRate,
    total: subscriptions.length,
  };
}

export function computeReviewAnalytics(submissions) {
  const pending = submissions.filter((s) => (s.status || 'pending') === 'pending').length;
  const approved = submissions.filter((s) => s.status === 'approved' || s.status === 'verified').length;
  const rejected = submissions.filter((s) => s.status === 'rejected').length;
  const total = submissions.length;
  const approvalRate = total ? Math.round((approved / total) * 100) : 0;

  const calorieBuckets = submissions.reduce(
    (acc, s) => {
      const cal = s.caloriesPer100g || 0;
      if (cal < 100) acc.low += 1;
      else if (cal < 300) acc.mid += 1;
      else acc.high += 1;
      return acc;
    },
    { low: 0, mid: 0, high: 0 }
  );

  const categoryBreakdown = Object.entries(
    submissions.reduce((acc, s) => {
      const cat = (s.categories || 'Uncategorized').split(',')[0].trim();
      acc[cat] = (acc[cat] || 0) + 1;
      return acc;
    }, {})
  )
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  const volumeTrend = Array.from({ length: 14 }).map((_, index) => {
    const day = subDays(new Date(), 13 - index);
    const label = format(day, 'MMM d');
    const count = submissions.filter((s) => s.createdAt && format(new Date(s.createdAt), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')).length;
    return { label, submissions: count };
  });

  return { pending, approved, rejected, total, approvalRate, calorieBuckets, categoryBreakdown, volumeTrend };
}

export function computeOperationsAnalytics(tickets, submissions, subscriptions) {
  const openTickets = tickets.filter((t) => t.status !== 'resolved' && t.status !== 'closed');
  const critical = openTickets.filter((t) => t.priority === 'critical' || t.priority === 'high').length;
  const pendingReviews = submissions.filter((s) => (s.status || 'pending') === 'pending').length;
  const subscriptionIssues = subscriptions.filter((s) => s.status !== 'active' || !s.isPremium).length;

  const priorityBreakdown = ['critical', 'high', 'medium', 'low'].map((priority) => ({
    priority,
    count: openTickets.filter((t) => (t.priority || 'medium') === priority).length,
  }));

  const caseMix = [
    { type: 'Support', count: openTickets.length },
    { type: 'Content review', count: pendingReviews },
    { type: 'Subscriptions', count: subscriptionIssues },
  ];

  const ticketTrend = Array.from({ length: 14 }).map((_, index) => {
    const day = subDays(new Date(), 13 - index);
    const label = format(day, 'MMM d');
    const count = tickets.filter((t) => t.createdAt && format(new Date(t.createdAt), 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')).length;
    return { label, tickets: count };
  });

  return { openTickets: openTickets.length, critical, pendingReviews, subscriptionIssues, priorityBreakdown, caseMix, ticketTrend, totalCases: openTickets.length + pendingReviews + subscriptionIssues };
}
